import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { attendanceAPI, overtimeAPI, bulletinAPI } from '../../services/api';
import { useNavigate } from 'react-router-dom';
import {
  Clock,
  CheckCircle,
  AlertCircle,
  Calendar,
  Timer,
  TrendingUp,
  Users,
  Hourglass,
  Pin,
  Plus,
  Pencil,
  Trash2,
  X,
  ImagePlus,
} from 'lucide-react';
import { format, parseISO } from 'date-fns';

const BULLETIN_DEFAULT_COUNT = 3;

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [todayStatus, setTodayStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Manager stats
  const [managerStats, setManagerStats] = useState(null);

  // Bulletin board
  const [posts, setPosts] = useState([]);
  const [showAll, setShowAll] = useState(false);
  const [showArchived, setShowArchived] = useState(false);
  const [postModal, setPostModal] = useState(null); // null | { mode: 'create' | 'edit', post?: {} }
  const [postForm, setPostForm] = useState({ title: '', body: '', is_pinned: false, image_filenames: [] });
  const [postImages, setPostImages] = useState([]); // [{ filename, url, preview }]
  const [postSaving, setPostSaving] = useState(false);
  const [lightbox, setLightbox] = useState(null); // { url, type: 'image'|'pdf' }
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchTodayStatus();
    fetchBulletinPosts();
    if (user?.is_manager || user?.is_hr || user?.is_admin) {
      fetchManagerStats();
    }
  }, []);

  const fetchTodayStatus = async () => {
    try {
      const response = await attendanceAPI.getTodayStatus();
      setTodayStatus(response.data);
    } catch (error) {
      setError('Failed to fetch today\'s status');
    } finally {
      setLoading(false);
    }
  };

  const fetchManagerStats = async () => {
    try {
      const today = format(new Date(), 'yyyy-MM-dd');
      const [overtimeRes, teamRes] = await Promise.all([
        overtimeAPI.getAllOvertimeRequests({ status: 'pending' }),
        attendanceAPI.getManagerTeamRecords({ start_date: today, end_date: today }),
      ]);

      const pendingOvertime = overtimeRes.data.overtime_requests?.length || 0;
      const teamRecords = teamRes.data.records || [];
      const totalTeam = new Set(teamRecords.map(r => r.user_id)).size;
      const presentToday = teamRecords.filter(r => r.clock_in).length;
      const absentToday = teamRecords.filter(r => !r.clock_in && !r.is_holiday && !r.leave_code).length;

      setManagerStats({ pendingOvertime, totalTeam, presentToday, absentToday });
    } catch {
      // non-critical, fail silently
    }
  };

  const fetchBulletinPosts = async (archived = false) => {
    try {
      const res = await bulletinAPI.getPosts(archived ? { archived: true } : undefined);
      setPosts(res.data.posts || []);
    } catch { /* non-critical */ }
  };

  const toggleArchiveView = () => {
    const next = !showArchived;
    setShowArchived(next);
    setShowAll(false);
    fetchBulletinPosts(next);
  };

  const handleArchivePost = async (post) => {
    try {
      await bulletinAPI.updatePost(post.id, { is_archived: !post.is_archived });
      fetchBulletinPosts(showArchived);
    } catch { alert('Failed to update post'); }
  };

  const openCreate = () => {
    setPostForm({ title: '', body: '', is_pinned: false, image_filenames: [] });
    setPostImages([]);
    setPostModal({ mode: 'create' });
  };

  const openEdit = (post) => {
    setPostForm({ title: post.title, body: post.body, is_pinned: post.is_pinned, image_filenames: post.images.map(i => i.filename) });
    setPostImages(post.images.map(i => ({ filename: i.filename, url: i.url, preview: null })));
    setPostModal({ mode: 'edit', post });
  };

  const handleImageSelect = async (e) => {
    const files = Array.from(e.target.files);
    for (const file of files) {
      const fd = new FormData();
      fd.append('file', file);
      try {
        const res = await bulletinAPI.uploadImage(fd);
        const { filename, url } = res.data;
        setPostImages(prev => [...prev, { filename, url, preview: URL.createObjectURL(file) }]);
        setPostForm(prev => ({ ...prev, image_filenames: [...prev.image_filenames, filename] }));
      } catch (err) {
        alert(err.response?.data?.error || 'Attachment upload failed');
      }
    }
    e.target.value = '';
  };

  const removeImage = (filename) => {
    setPostImages(prev => prev.filter(i => i.filename !== filename));
    setPostForm(prev => ({ ...prev, image_filenames: prev.image_filenames.filter(f => f !== filename) }));
  };

  const handlePostSubmit = async (e) => {
    e.preventDefault();
    setPostSaving(true);
    try {
      if (postModal.mode === 'create') {
        await bulletinAPI.createPost(postForm);
      } else {
        await bulletinAPI.updatePost(postModal.post.id, postForm);
      }
      setPostModal(null);
      fetchBulletinPosts();
    } catch (err) {
      alert(err.response?.data?.error || 'Failed to save post');
    } finally {
      setPostSaving(false);
    }
  };

  const handleDeletePost = async (post) => {
    if (!window.confirm(`Delete "${post.title}"?`)) return;
    try {
      await bulletinAPI.deletePost(post.id);
      fetchBulletinPosts();
    } catch { alert('Failed to delete post'); }
  };

  const canEdit = user?.is_hr || user?.is_admin;
  const visiblePosts = showAll ? posts : posts.slice(0, BULLETIN_DEFAULT_COUNT);

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-600 bg-green-100';
      case 'in_progress': return 'text-blue-600 bg-blue-100';
      default: return 'text-gray-600 bg-gray-100';
    }
  };

  const getStatusText = (status) => {
    switch (status) {
      case 'completed': return 'Work Complete';
      case 'in_progress': return 'Currently Working';
      default: return 'Not Started';
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">

      {/* Bulletin Board */}
      <div className="bg-white shadow rounded-lg overflow-hidden">
        <div className="px-4 py-4 border-b border-gray-100 flex items-center justify-between gap-2">
          <h2 className="text-lg font-semibold text-gray-900">
            Announcements {showArchived && <span className="ml-2 text-xs font-normal text-amber-600 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded-full">Archived</span>}
          </h2>
          <div className="flex items-center gap-2">
            {canEdit && (
              <button onClick={toggleArchiveView} className={`px-3 py-1.5 text-sm rounded-lg border transition-colors ${showArchived ? 'bg-amber-50 border-amber-300 text-amber-700 hover:bg-amber-100' : 'border-gray-300 text-gray-600 hover:bg-gray-50'}`}>
                {showArchived ? 'View active' : 'View archived'}
              </button>
            )}
            {canEdit && !showArchived && (
              <button onClick={openCreate} className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700">
                <Plus className="h-4 w-4" /> New Post
              </button>
            )}
          </div>
        </div>

        {posts.length === 0 ? (
          <div className="px-4 py-8 text-center text-gray-400 text-sm">{showArchived ? 'No archived posts.' : 'No announcements yet.'}</div>
        ) : (
          <>
            <div className="divide-y divide-gray-100">
              {visiblePosts.map(post => (
                <div key={post.id} className={`px-4 py-4 ${post.is_archived ? 'opacity-60' : ''}`}>
                  <div className="flex items-start justify-between gap-2">
                    <div className="flex items-center gap-2 min-w-0">
                      {post.is_pinned && <Pin className="h-4 w-4 text-blue-500 flex-shrink-0" />}
                      {post.is_archived && <span className="text-xs text-amber-600 bg-amber-50 border border-amber-200 px-1.5 py-0.5 rounded flex-shrink-0">Archived</span>}
                      <span className="font-semibold text-gray-900 truncate">{post.title}</span>
                    </div>
                    {canEdit && (
                      <div className="flex items-center gap-2 flex-shrink-0">
                        {!post.is_archived && <button onClick={() => openEdit(post)} className="text-gray-400 hover:text-blue-600"><Pencil className="h-4 w-4" /></button>}
                        <button onClick={() => handleArchivePost(post)} title={post.is_archived ? 'Unarchive' : 'Archive'} className="text-gray-400 hover:text-amber-600 text-xs border border-gray-200 rounded px-1.5 py-0.5 hover:border-amber-300">
                          {post.is_archived ? 'Unarchive' : 'Archive'}
                        </button>
                        <button onClick={() => handleDeletePost(post)} className="text-gray-400 hover:text-red-600"><Trash2 className="h-4 w-4" /></button>
                      </div>
                    )}
                  </div>
                  <p className="text-xs text-gray-400 mt-0.5">{post.author} · {format(parseISO(post.created_at), 'dd MMM yyyy, HH:mm')}</p>
                  <p className="mt-2 text-sm text-gray-700 whitespace-pre-wrap">{post.body}</p>
                  {post.images.length > 0 && (
                    <div className="mt-3 flex flex-wrap gap-2">
                      {post.images.map(img => {
                        const isPdf = img.filename.toLowerCase().endsWith('.pdf');
                        return isPdf ? (
                          <button key={img.id} onClick={() => setLightbox({ url: img.url, type: 'pdf' })}
                            className="flex items-center gap-1.5 px-3 py-2 border border-gray-200 rounded-lg text-sm text-gray-700 hover:bg-gray-50 bg-white">
                            📄 {img.filename}
                          </button>
                        ) : (
                          <img key={img.id} src={img.url} alt="" onClick={() => setLightbox({ url: img.url, type: 'image' })}
                            className="h-32 w-auto rounded-lg object-cover border border-gray-200 cursor-pointer hover:opacity-90 transition-opacity" />
                        );
                      })}
                    </div>
                  )}
                </div>
              ))}
            </div>
            {posts.length > BULLETIN_DEFAULT_COUNT && (
              <div className="px-4 py-3 border-t border-gray-100 text-center">
                <button onClick={() => setShowAll(v => !v)} className="text-sm text-blue-600 hover:underline">
                  {showAll ? 'Show less' : `View all ${posts.length} posts`}
                </button>
              </div>
            )}
          </>
        )}
      </div>

      {/* Post Modal */}
      {postModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
              <h3 className="font-semibold text-gray-900">{postModal.mode === 'create' ? 'New Announcement' : 'Edit Announcement'}</h3>
              <button onClick={() => setPostModal(null)}><X className="h-5 w-5 text-gray-400" /></button>
            </div>
            <form onSubmit={handlePostSubmit} className="px-5 py-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                <input
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                  value={postForm.title}
                  onChange={e => setPostForm(p => ({ ...p, title: e.target.value }))}
                  required
                  autoFocus
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Body</label>
                <textarea
                  rows={5}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 resize-y"
                  value={postForm.body}
                  onChange={e => setPostForm(p => ({ ...p, body: e.target.value }))}
                  required
                />
              </div>
              <div className="flex items-center gap-2">
                <input type="checkbox" id="pinPost" checked={postForm.is_pinned} onChange={e => setPostForm(p => ({ ...p, is_pinned: e.target.checked }))} className="h-4 w-4 text-blue-600 border-gray-300 rounded" />
                <label htmlFor="pinPost" className="text-sm text-gray-700">Pin this announcement</label>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Images</label>
                {postImages.length > 0 && (
                  <div className="flex flex-wrap gap-2 mb-2">
                    {postImages.map(img => (
                      <div key={img.filename} className="relative">
                        <img src={img.preview || img.url} alt="" className="h-20 w-20 object-cover rounded-lg border border-gray-200" />
                        <button type="button" onClick={() => removeImage(img.filename)} className="absolute -top-1.5 -right-1.5 bg-red-500 text-white rounded-full p-0.5 hover:bg-red-600">
                          <X className="h-3 w-3" />
                        </button>
                      </div>
                    ))}
                  </div>
                )}
                <button type="button" onClick={() => fileInputRef.current?.click()} className="flex items-center gap-1.5 px-3 py-1.5 border border-dashed border-gray-300 rounded-lg text-sm text-gray-500 hover:border-blue-400 hover:text-blue-500">
                  <ImagePlus className="h-4 w-4" /> Add images
                </button>
                <input ref={fileInputRef} type="file" accept="image/*,application/pdf" multiple className="hidden" onChange={handleImageSelect} />
              </div>
              <div className="flex justify-end gap-3 pt-2">
                <button type="button" onClick={() => setPostModal(null)} className="px-4 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50">Cancel</button>
                <button type="submit" disabled={postSaving} className="px-4 py-2 bg-blue-600 text-white rounded-lg text-sm hover:bg-blue-700 disabled:opacity-50">{postSaving ? 'Saving…' : 'Save'}</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Lightbox */}
      {lightbox && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 p-4" onClick={() => setLightbox(null)}>
          {lightbox.type === 'pdf' ? (
            <iframe src={lightbox.url} title="PDF viewer" className="w-full max-w-4xl h-[90vh] rounded-lg shadow-2xl bg-white" onClick={e => e.stopPropagation()} />
          ) : (
            <img src={lightbox.url} alt="" className="max-h-[90vh] max-w-[90vw] rounded-lg shadow-2xl object-contain" onClick={e => e.stopPropagation()} />
          )}
          <button onClick={() => setLightbox(null)} className="absolute top-4 right-4 text-white bg-black/40 rounded-full p-1.5 hover:bg-black/60">
            <X className="h-5 w-5" />
          </button>
        </div>
      )}

      {/* Manager Team Stats */}
      {managerStats && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">Team Overview — Today</h3>
            <div className="grid grid-cols-2 gap-4 sm:grid-cols-4">
              <button
                onClick={() => navigate('/manager')}
                className="text-left p-4 bg-yellow-50 border border-yellow-200 rounded-lg hover:bg-yellow-100 transition-colors"
              >
                <div className="flex items-center space-x-3">
                  <Hourglass className="h-6 w-6 text-yellow-500 flex-shrink-0" />
                  <div>
                    <p className="text-2xl font-bold text-yellow-700">{managerStats.pendingOvertime}</p>
                    <p className="text-xs text-yellow-600 font-medium">Pending Overtime</p>
                  </div>
                </div>
              </button>

              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <Users className="h-6 w-6 text-blue-500 flex-shrink-0" />
                  <div>
                    <p className="text-2xl font-bold text-blue-700">{managerStats.totalTeam}</p>
                    <p className="text-xs text-blue-600 font-medium">Team Size</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <CheckCircle className="h-6 w-6 text-green-500 flex-shrink-0" />
                  <div>
                    <p className="text-2xl font-bold text-green-700">{managerStats.presentToday}</p>
                    <p className="text-xs text-green-600 font-medium">Present Today</p>
                  </div>
                </div>
              </div>

              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <div className="flex items-center space-x-3">
                  <AlertCircle className="h-6 w-6 text-red-500 flex-shrink-0" />
                  <div>
                    <p className="text-2xl font-bold text-red-700">{managerStats.absentToday}</p>
                    <p className="text-xs text-red-600 font-medium">Absent Today</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Welcome Header */}
      {/* <div className="bg-white overflow-hidden shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6">
          <div className="flex items-center">
            <div className="flex-shrink-0">
              <div className="h-12 w-12 bg-blue-600 rounded-full flex items-center justify-center">
                <Clock className="h-6 w-6 text-white" />
              </div>
            </div>
            <div className="ml-5 w-0 flex-1">
              <dl>
                <dt className="text-sm font-medium text-gray-500 truncate">
                  Welcome back, {user?.name}
                </dt>
                <dd className="text-lg font-medium text-gray-900">
                  {format(new Date(), 'EEEE, MMMM do, yyyy')}
                </dd>
              </dl>
            </div>
          </div>
        </div>
      </div> */}

      {/* Error Message */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-md p-4 flex items-center">
          <AlertCircle className="h-5 w-5 text-red-400 mr-2" />
          <span className="text-red-700 text-sm">{error}</span>
        </div>
      )}

      {/* Today's Status Cards */}
      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {/* Status Card */}
        {/* <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircle className="h-6 w-6 text-gray-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Today's Status
                  </dt>
                  <dd className={`text-sm font-medium px-2 py-1 rounded-full inline-block ${getStatusColor(todayStatus?.status)}`}>
                    {getStatusText(todayStatus?.status)}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div> */}

        {/* Clock In Time */}
        {/* <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Timer className="h-6 w-6 text-green-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Clock In
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {todayStatus?.clock_in 
                      ? format(parseISO(todayStatus.clock_in), 'HH:mm')
                      : '--:--'
                    }
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div> */}

        {/* Clock Out Time */}
        {/* <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <Timer className="h-6 w-6 text-red-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Clock Out
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {todayStatus?.clock_out 
                      ? format(parseISO(todayStatus.clock_out), 'HH:mm')
                      : '--:--'
                    }
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div> */}

        {/* Work Hours */}
        {/* <div className="bg-white overflow-hidden shadow rounded-lg">
          <div className="p-5">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <TrendingUp className="h-6 w-6 text-blue-400" />
              </div>
              <div className="ml-5 w-0 flex-1">
                <dl>
                  <dt className="text-sm font-medium text-gray-500 truncate">
                    Work Hours
                  </dt>
                  <dd className="text-lg font-medium text-gray-900">
                    {todayStatus?.total_work_minutes 
                      ? `${Math.floor(todayStatus.total_work_minutes / 60)}h ${todayStatus.total_work_minutes % 60}m`
                      : '0h 0m'
                    }
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </div> */}
      </div>


      {/* Shift Information */}
      {/* {todayStatus?.shift && (
        <div className="bg-white shadow rounded-lg">
          <div className="px-4 py-5 sm:p-6">
            <h3 className="text-lg leading-6 font-medium text-gray-900 mb-4">
              Shift Information
            </h3>
            
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
              <div>
                <dt className="text-sm font-medium text-gray-500">Shift Name</dt>
                <dd className="mt-1 text-sm text-gray-900">{todayStatus.shift.name}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">Start Time</dt>
                <dd className="mt-1 text-sm text-gray-900">{todayStatus.shift.start_time}</dd>
              </div>
              <div>
                <dt className="text-sm font-medium text-gray-500">End Time</dt>
                <dd className="mt-1 text-sm text-gray-900">{todayStatus.shift.end_time}</dd>
              </div>
            </div>

            {(todayStatus.is_late || todayStatus.is_early_out) && (
              <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-md">
                <div className="flex">
                  <AlertCircle className="h-5 w-5 text-yellow-400" />
                  <div className="ml-3">
                    <h3 className="text-sm font-medium text-yellow-800">
                      Attendance Notice
                    </h3>
                    <div className="mt-2 text-sm text-yellow-700">
                      {todayStatus.is_late && <p>• Late arrival detected</p>}
                      {todayStatus.is_early_out && <p>• Early departure detected</p>}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )} */}
    </div>
  );
};

export default Dashboard;
